import { memo, useCallback, useRef, useState } from 'react'
import { ActionMenu } from 'cio-design-library/client-components'
import Icon24 from 'builder/components/Icon'
import { FORMATS } from 'builder/modules/constants'
import { useI18n } from 'builder/hooks/useI18n'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { DownloadDocumentHandler } from 'builder/modules/resumeEditor'
import { ButtonV2 } from 'builder/components/Button'
import { Container, IconChevronDown } from './styles'

const LOCALIZATION_KEY = 'builder.resume_editor.document_download'

export type Props = {
  handleShareDocument?: () => void
  handleCombineDocuments?: () => void
  handleDownloadDocument: DownloadDocumentHandler
  showResumeDistributionModal?: (format: FORMATS) => void
  isDownloading?: boolean
  handleDownloadButtonMenuOpen?: () => void
  menuPosition?: 'bottom' | 'top'
  menuAlign?: 'left' | 'right'
  shouldShowIcon?: boolean
}

export const DownloadButton = memo((props: Props) => {
  const {
    handleDownloadDocument,
    handleShareDocument,
    showResumeDistributionModal,
    isDownloading,
    handleCombineDocuments,
    handleDownloadButtonMenuOpen,
    menuPosition = 'bottom',
    menuAlign = 'right',
    shouldShowIcon = true,
  } = props
  const { i18n } = useI18n()
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState<boolean>(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const isSharingEnabled = !!handleShareDocument
  const isCombineEnabled = !!handleCombineDocuments

  const handleButtonClick = useCallback(() => {
    if (!isDownloadMenuOpen && handleDownloadButtonMenuOpen) {
      handleDownloadButtonMenuOpen()
    }

    setIsDownloadMenuOpen(!isDownloadMenuOpen)
  }, [isDownloadMenuOpen, handleDownloadButtonMenuOpen])

  const handleDownloadDocumentClick: DownloadDocumentHandler = useCallback(
    ({ format = FORMATS.pdf } = {}) => {
      setIsDownloadMenuOpen(false)

      if (showResumeDistributionModal) {
        showResumeDistributionModal(format)
        return
      }

      handleDownloadDocument({ format })
    },
    [handleDownloadDocument, showResumeDistributionModal],
  )

  const handleShareClick = useCallback(() => {
    setIsDownloadMenuOpen(false)

    if (handleShareDocument) handleShareDocument()
  }, [handleShareDocument])

  const handleCombineClick = useCallback(() => {
    setIsDownloadMenuOpen(false)
    if (handleCombineDocuments) handleCombineDocuments()
  }, [handleCombineDocuments])

  const closeMenu = useCallback(() => {
    setIsDownloadMenuOpen(false)
  }, [])

  useEscapeHandler(closeMenu)

  return (
    <Container>
      <ButtonV2
        ref={buttonRef}
        size="small"
        id="builder-download-button"
        trailingIcon={
          shouldShowIcon ? (
            <IconChevronDown
              style={{
                transform: isDownloadMenuOpen ? 'rotateX(180deg)' : 'rotateX(0deg)',
              }}
            />
          ) : undefined
        }
        disabled={isDownloading}
        onClick={handleButtonClick}
      >
        {i18n.t(`${LOCALIZATION_KEY}.download_button_text`)}
      </ButtonV2>
      {isDownloadMenuOpen && (
        <ActionMenu
          position={menuPosition}
          align={menuAlign}
          clickOutsideExceptRefs={[buttonRef]}
          onClose={closeMenu}
          actions={[
            {
              text: i18n.t(`${LOCALIZATION_KEY}.options.pdf`),
              icon: <Icon24.PdfStaticColor />,
              onClick: () => handleDownloadDocumentClick({ format: FORMATS.pdf }),
            },
            {
              text: i18n.t(`${LOCALIZATION_KEY}.options.docx`),
              icon: <Icon24.DocxStaticColor />,
              onClick: () => handleDownloadDocumentClick({ format: FORMATS.docx }),
            },
            {
              text: i18n.t(`${LOCALIZATION_KEY}.options.txt`),
              icon: <Icon24.TxtStaticColor />,
              onClick: () => handleDownloadDocumentClick({ format: FORMATS.txt }),
            },
            ...(isCombineEnabled
              ? [
                  {
                    text: i18n.t('builder.resume_editor.combine_with'),
                    icon: <Icon24.Combine />,
                    onClick: handleCombineClick,
                  },
                ]
              : []),
            ...(isSharingEnabled
              ? [
                  {
                    text: i18n.t(`${LOCALIZATION_KEY}.options.share_resume`),
                    icon: <Icon24.Link />,
                    divider: true,
                    onClick: handleShareClick,
                    description: i18n.t(`${LOCALIZATION_KEY}.options.share_resume_description`),
                  },
                ]
              : []),
          ]}
        />
      )}
    </Container>
  )
})
